// Source dictionary — `TextKey` is derived from this file (`keyof typeof en`), so
// es.ts must translate every key exactly or fail to compile. Key convention:
// "component.descriptiveName"; "field.*"/"kind.*"/"section.*" are SWMM's own
// domain vocabulary (see fieldLabels.ts). Values may carry "{name}"-style
// placeholders, interpolated by t()/translate() in hooks.ts.

export const en = {
  // --- SettingsMenu.tsx (theme + language + build commit) ---
  "theme.toDarkMode": "Switch to dark mode",
  "theme.toLightMode": "Switch to light mode",
  "theme.light": "Light",
  "theme.dark": "Dark",
  "common.language": "Language",
  "settings.title": "Settings",
  "settings.theme": "Theme",
  "settings.version": "Version",

  // --- Landing.tsx ---
  "landing.title": "FLUIT Storm",
  "landing.tagline": "Hydraulic modeling of urban drainage networks · SWMM 5 compatible",
  "landing.openButton": "Open file",
  "landing.newEmptyButton": "Create project",
  "landing.orTryAnExample": "Or try an example",
  "landing.sampleVictoriaName": "Victoria (Canada)",
  "landing.sampleHagueName": "The Hague (Netherlands)",
  "landing.sampleChaohuName": "Chaohu (China)",
  "landing.sampleBogotaName": "Bogotá (Colombia)",
  "landing.trySampleError": "Couldn't load the example",
  "landing.openFileError": "Couldn't open that file: {message}",
  "landing.untitledProjectName": "Untitled project",
  "landing.yourProjects": "Your projects",
  "landing.savedProjectNotFoundTitle": "Project not found",
  "landing.savedProjectNotFoundMessage":
    "The project this link points to no longer exists in this browser — it may have been deleted, or the link was opened on a different device or browser.",
  "landing.deleteProject": "Delete {name}",
  "landing.newProjectTitle": "New Project",
  "landing.projectNameLabel": "Project name",
  "landing.back": "Back",
  "landing.open": "Open",

  // --- ProjectionPicker.tsx ---
  "projectionPicker.georeferencedLabel": "This project is georeferenced",
  "projectionPicker.searchPlaceholder":
    "Search by name or EPSG code (e.g. “UTM 30N”, “ETRS89”, “25830”), or paste a proj4 string",
  "projectionPicker.searching": "Searching…",
  "projectionPicker.searchFailed": "Search failed: {message}",
  "projectionPicker.hint": "Type to search, then pick a projection from the list.",
  "projectionPicker.warning": "{reason} Opening in plane mode unless you pick a different one.",
  "projectionPicker.customProjection": "Custom projection",

  // --- validateCrs.ts (shown via ProjectionPicker.tsx's warning above) ---
  "crsValidation.cantConvert": "This projection can't convert these coordinates.",
  "crsValidation.invalidRange": "These coordinates don't look valid for this projection.",
  "crsValidation.outsideAreaOfUse":
    "These coordinates fall well outside this projection's area of use.",
  "crsValidation.generic": "This doesn't look right for these coordinates.",

  // --- App.tsx topbar ---
  "toolRail.label": "Tools",
  "toolbar.toggleElementList": "Show/hide element list",
  "toolbar.search": "Find element",
  "toolbar.searchPlaceholder": "Element name…",
  "toolbar.searchNoMatches": "No matches",
  "toolbar.undo": "Undo",
  "toolbar.redo": "Redo",
  "toolbar.editGeometry": "Edit geometry",
  "toolbar.multiSelectMode": "Multiple selection",
  "toolbar.selectionTools": "Selection tools",
  "selectionTools.rectangle": "Rectangle",
  "selectionTools.lasso": "Lasso",
  "selectionTools.byType": "By type",
  "selectionTools.byQuery": "By query",
  "toolbar.addElement": "Add element",
  "toolbar.addJunction": "Add Junction",
  "toolbar.addOutfall": "Add Outfall",
  "toolbar.addStorage": "Add Storage",
  "toolbar.addDivider": "Add Divider",
  "toolbar.addRaingage": "Add Raingage",
  "toolbar.addConduit": "Add Conduit",
  "toolbar.addPump": "Add Pump",
  "toolbar.addOrifice": "Add Orifice",
  "toolbar.addWeir": "Add Weir",
  "toolbar.addOutlet": "Add Outlet",
  "toolbar.addSubcatchment": "Add Subcatchment",
  "toolbar.toPlaneView": "Switch to plane view",
  "toolbar.toMapView": "Switch to map view",
  "toolbar.toMapViewOsmDisclosure":
    "Map view loads background tiles from OpenStreetMap, which sees your device's IP address and this project's approximate location.",
  "toolbar.runSimulation": "Run Simulation",
  "toolbar.cancelSimulation": "Cancel simulation",
  "simulation.failedTitle": "Simulation failed",
  "simulation.failedMessage": "The simulation could not complete: {message}",
  "simulation.workerCrashedMessage": "The simulation stopped unexpectedly. Try running it again.",
  "simulation.phaseRunoff": "Runoff",
  "simulation.phaseRouting": "Routing",
  "simulation.phaseReport": "Generating report…",
  "simulation.warningsTitle": "Simulation completed with {count} warnings",
  "simulation.warningsTitleOne": "Simulation completed with {count} warning",
  "externalFiles.title": "External files needed",
  "externalFiles.hint":
    "This project reads rainfall/climate data from {count} external files referenced by its own .inp — not embedded in it. Supply each one below to use its real data, or run without it (that source reports zero rainfall throughout instead).",
  "externalFiles.hintOne":
    "This project reads rainfall/climate data from {count} external file referenced by its own .inp — not embedded in it. Supply it below to use its real data, or run without it (that source reports zero rainfall throughout instead).",
  "externalFiles.rowLabel": '{section} "{name}" → {filename}',
  "externalFiles.chooseFile": "Choose file…",
  "externalFiles.provided": "Provided: {fileName}",
  "externalFiles.replace": "Replace…",
  "externalFiles.runButton": "Run Simulation",
  "storage.errorTitle": "Could not save",
  "storage.errorMessage":
    "Your changes could not be saved to browser storage: {message}. Keep this tab open and consider exporting your project (.inp) as a backup — reloading the page may lose unsaved edits.",
  "toolbar.simulationSummary": "Simulation Summary",
  "toolbar.statisticsReport": "Statistics Report",
  "toolbar.resultsAnimation": "Animate Results on Map",
  "toolbar.resultsDashboard": "Results Dashboard",
  "toolbar.longitudinalProfile": "Longitudinal Profile",
  "toolbar.thematicMap": "Thematic Map",
  "toolbar.mapQuery": "Map Query",
  "toolbar.tools": "Tools",
  "toolbar.measureDistance": "Measure distance",
  "measureTool.total": "Total:",
  "toolbar.georeference": "Georeference by control points",
  "georeference.title": "Georeference by control points",
  "georeference.hint":
    "Pick 2 or more nodes/rain gauges and mark where they really are on the map below. The project's coordinates will be rewritten into real-world (WGS84) degrees — this can't be undone by re-opening this tool, only with the regular Undo.",
  "georeference.osmMapDisclosure":
    "The map below loads background tiles from OpenStreetMap, which sees your device's IP address and this project's approximate location.",
  "georeference.pickElementPlaceholder": "Node or rain gauge…",
  "georeference.noTargetYet": "Click on the map",
  "georeference.targetCoords": "{lat}, {lon}",
  "georeference.residual": "±{meters} m",
  "georeference.removePoint": "Remove this control point",
  "georeference.addPoint": "Add point",
  "georeference.apply": "Georeference",
  "georeference.warningsTitle": "Some coordinates couldn't be transformed",
  "malformedLinks.warningsTitle": "Some malformed link rows were dropped",
  "toolbar.exportInp": "Export .inp",
  "toolbar.closeProject": "Close project",
  "projectMenu.title": "Project options",
  "projectMenu.rename": "Rename",
  "projectMenu.duplicate": "Save copy as",
  "projectMenu.delete": "Delete this project",
  "projectMenu.renameTitle": "Rename project",
  "projectMenu.duplicateTitle": "Save copy as",
  "projectMenu.copyNameSuffix": "{name} (copy)",

  // --- ExampleBanner.tsx ---
  "exampleBanner.message":
    "You're viewing an example — changes aren't saved unless you make a copy.",
  "sampleAttribution.hague":
    "Sewer network for The Hague, Netherlands (adapted copy) — via GNN-UDS, GPL-3.0.",
  "sampleAttribution.chaohu":
    "Drainage network for Chaohu, China (probable real network, source paper unconfirmed) — via GNN-UDS, GPL-3.0.",
  "sampleAttribution.bogota":
    '"Red Chico Sur" drainage network, Bogotá, Colombia — via MatSWMM, GPL-3.0.',
  "exampleBanner.sourceLink": "Source",
  "exampleBanner.attribution": "Attribution",
  "projectMenu.deleteTitle": "Delete project",
  "projectMenu.deleteMessage": '"{name}" will be permanently deleted. This can\'t be undone.',
  "projectMenu.deleteConfirm": "Delete permanently",
  "projectMenu.simulationOptions": "Simulation options",
  "projectMenu.elementDefaults": "Defaults for new elements",
  "projectMenu.importLayers": "Import GIS layers",
  "projectMenu.projectData": "Project data",
  "projectMenu.backgroundImage": "Background Image",
  "projectData.groupData": "Common data",
  "projectData.groupHydrology": "Hydrology",
  "projectData.groupHydraulics": "Hydraulics",
  "projectData.groupQuality": "Water quality",
  "projectData.groupControl": "Control",
  "projectData.tabCurves": "Curves",
  "projectData.addCurve": "Add curve",
  "projectData.deleteCurve": "Delete curve",
  "projectData.noCurveSelected": "Select a curve, or add a new one.",
  "projectData.curveType": "Type",
  "projectData.addPoint": "Add point",
  "projectData.valuesTable": "Values table",
  "projectData.chartPreview": "Chart",
  "projectData.tabTimeSeries": "Time series",
  "projectData.addTimeSeries": "Add time series",
  "projectData.deleteTimeSeries": "Delete time series",
  "projectData.noTimeSeriesSelected": "Select a time series, or add a new one.",
  "projectData.date": "Date",
  "projectData.time": "Time",
  "projectData.value": "Value",
  "projectData.tabPatterns": "Patterns",
  "projectData.addPattern": "Add pattern",
  "projectData.deletePattern": "Delete pattern",
  "projectData.noPatternSelected": "Select a pattern, or add a new one.",
  "projectData.patternType": "Type",
  "projectData.patternPeriod": "Period",
  "projectData.tabPollutants": "Pollutants",
  "projectData.addPollutant": "Add pollutant",
  "projectData.deletePollutant": "Delete pollutant",
  "projectData.noPollutantSelected": "Select a pollutant, or add a new one.",
  "projectData.pollutantUnits": "Units",
  "projectData.pollutantRainConc": "Rain concentration",
  "projectData.pollutantGwConc": "Groundwater concentration",
  "projectData.pollutantIiConc": "I&I concentration",
  "projectData.pollutantDecayCoeff": "Decay coefficient",
  "projectData.pollutantSnowOnly": "Snow only",
  "projectData.pollutantCoPollutant": "Co-pollutant",
  "projectData.pollutantCoFraction": "Co-fraction",
  "projectData.pollutantDwfConc": "Dry weather concentration",
  "projectData.pollutantInitConc": "Initial concentration",
  "projectData.tabLandUses": "Land uses",
  "projectData.addLandUse": "Add land use",
  "projectData.deleteLandUse": "Delete land use",
  "projectData.noLandUseSelected": "Select a land use, or add a new one.",
  "projectData.sweepingInterval": "Sweeping interval (days)",
  "projectData.sweepingAvailability": "Sweeping availability",
  "projectData.lastSweptDay": "Last swept (days before start)",
  "projectData.noPollutantsYet":
    "Add a pollutant first (Pollutants tab) to configure buildup/washoff.",
  "projectData.tabBuildup": "Buildup",
  "projectData.tabWashoff": "Washoff",
  "projectData.pollutant": "Pollutant",
  "projectData.buildupFunction": "Function",
  "projectData.washoffFunction": "Function",
  "projectData.coeff1": "Coeff. 1",
  "projectData.coeff2": "Coeff. 2",
  "projectData.coeff3": "Coeff. 3",
  "projectData.normalizer": "Normalizer",
  "projectData.cleaningEfficiency": "Cleaning effic.",
  "projectData.bmpEfficiency": "BMP effic.",
  "common.name": "Name",
  // --- App.tsx's own delete flow (Delete/Supr key) — always the CATEGORY
  // word (node/link/subcatchment/raingage), never a specific kind
  // (junction/outfall/conduit/pump/...) — the same generic terms
  // elementBrowser.nodes/links already group the tree by, so an element
  // mentioned here reads the same way it does there, not a different,
  // more specific word depending on which dialog happens to name it. ---
  "category.node": "node",
  "category.link": "link",
  "category.subcatchment": "subcatchment",
  "category.raingage": "raingage",
  "delete.confirmTitle": "Delete {category}",
  "delete.confirmMessage": 'Delete {category} "{name}"?',
  "delete.blockedTitle": "Can't delete {category}",
  // A blocked node only ever lists links, never anything else — singular/
  // plural instead of one generic template, for correct verb agreement.
  "delete.blockedNodeConnectedOne": 'Node "{name}" is still connected to link {items}.',
  "delete.blockedNodeConnectedMany": 'Node "{name}" is still connected to links {items}.',
  "delete.blockedRaingageUsedOne": 'Raingage "{name}" is still used by subcatchment {items}.',
  "delete.blockedRaingageUsedMany": 'Raingage "{name}" is still used by subcatchments {items}.',
  "delete.blockedOutletUsedOne": '"{name}" is still used as the outlet of subcatchment {items}.',
  "delete.blockedOutletUsedMany": '"{name}" is still used as the outlet of subcatchments {items}.',
  "delete.blockedControlRuleOne": '"{name}" is still referenced by control rule {items}.',
  "delete.blockedControlRuleMany": '"{name}" is still referenced by control rules {items}.',
  // A deleted link can only ever orphan its 2 end nodes at most — same
  // singular/plural reasoning as above.
  "delete.confirmLinkOrphanWarningOne":
    'Deleting link "{name}" will also delete node {items}, which would be left with no connections. Continue?',
  "delete.confirmLinkOrphanWarningMany":
    'Deleting link "{name}" will also delete nodes {items}, which would be left with no connections. Continue?',
  "common.and": "and",
  "common.andNMore": "and {count} more",
  "delete.confirmButton": "Delete",
  "delete.blockedGroupTitle": "Can't delete selection",
  "delete.blockedGroupMessage":
    "{items} still connected to a link outside the selection. Deselect it, or select that link too.",
  "delete.confirmGroupTitle": "Delete {count} elements",
  "delete.confirmGroupMessage": "Delete the {count} selected elements?",
  "delete.confirmGroupOrphanWarning":
    "Deleting the {count} selected elements will also delete {items}, which would be left with no connections. Continue?",
  "multiSelect.count": "{count} selected",
  "multiSelect.groupEdit": "Edit selection",
  "multiSelect.delete": "Delete selection",
  "groupEdit.title": "Edit {count} Selected Elements",
  "groupEdit.noProperties": "The selected elements have no common editable property.",
  "groupEdit.property": "Property",
  "groupEdit.newValue": "New value",
  "groupEdit.apply": "Apply to all",
  "gisImport.title": "Import GIS Layers",
  "gisImport.hint":
    "Pick a folder — every .geojson/.json file and Shapefile set (.shp+.dbf+.shx, .prj recommended) in it will be listed below as a layer you can import.",
  "gisImport.pickFolder": "Choose folder",
  "gisImport.reading": "Reading folder…",
  "gisImport.readFailed": "Couldn't read that folder: {message}",
  "gisImport.layerReadFailed": "{fileName}: couldn't read that file — {message}",
  "gisImport.noneFound": "No .geojson/.json files found in that folder.",
  "gisImport.columnFile": "File",
  "gisImport.columnGeometry": "Geometry",
  "gisImport.columnFeatures": "Features",
  "gisImport.columnType": "Import as",
  "gisImport.skip": "Skip (don't import)",
  "gisImport.mixedGeometry": "Mixed geometry — skip",
  "gisImport.nameField": "Name",
  "gisImport.useDefault": "Use default ({value})",
  "gisImport.importButton": "Import",
  "gisImport.summaryLine": "{file}: {created} imported, {skipped} skipped",
  "gisImport.placeholderSummary":
    "{count} imported elements still have default values — look for the marker in the element list to find and fill them in.",
  "gisImport.placeholderSummaryOne":
    "{count} imported element still has a default value — look for the marker in the element list to find and fill it in.",
  "gisImport.missingPrj":
    "Missing .prj file — the source coordinate system is unknown, so this layer can't be imported.",
  "gisImport.prjUnparseable":
    "The .prj file's coordinate system definition couldn't be read — this layer can't be imported.",
  "gisImport.unknownProjectCrsBlocked":
    "This project already has elements but no assigned coordinate system, so an imported layer's real-world coordinates can't be safely combined with it. Create or open the project again with a coordinate system assigned, then import.",
  "common.save": "Save",

  // --- OptionsDialog.tsx ---
  "optionsDialog.title": "Simulation Options",
  "optionsDialog.general": "General",
  "optionsDialog.flowUnits": "Flow units",
  "optionsDialog.flowRouting": "Flow routing",
  "optionsDialog.minSlope": "Minimum conduit slope (%)",
  "optionsDialog.simulationPeriod": "Simulation period",
  "optionsDialog.startDate": "Start date",
  "optionsDialog.startTime": "Start time",
  "optionsDialog.endDate": "End date",
  "optionsDialog.endTime": "End time",
  "optionsDialog.reportStartDate": "Report start date",
  "optionsDialog.reportStartTime": "Report start time",
  "optionsDialog.timeSteps": "Time steps",
  "optionsDialog.reportStep": "Report step",
  "optionsDialog.wetStep": "Wet weather step",
  "optionsDialog.dryStep": "Dry weather step",
  "optionsDialog.routingStep": "Routing step",
  "optionsDialog.dynamicWave": "Dynamic wave",
  "optionsDialog.normalFlowLimited": "Normal flow limited",
  "optionsDialog.allowPonding": "Allow ponding",
  "optionsDialog.variableStep": "Variable time step (safety factor)",
  "optionsDialog.minimumStep": "Minimum time step (sec)",
  "optionsDialog.maxTrials": "Max. trials per time step",
  "optionsDialog.headTolerance": "Head convergence tolerance",
  "optionsDialog.minSurfArea": "Minimum surface area",
  "optionsDialog.lengtheningStep": "Conduit lengthening time step",
  "defaultsDialog.title": "Defaults for New Elements",
  "defaultsDialog.idLabels": "ID Labels",
  "defaultsDialog.subcatchment": "Subcatchment",
  "defaultsDialog.nodeLink": "Nodes/Links",
  "defaultsDialog.raingage": "Rain Gages",
  "defaultsDialog.subcatchmentPrefix": "Subcatchments",
  "defaultsDialog.junction": "Junctions",
  "defaultsDialog.outfall": "Outfalls",
  "defaultsDialog.divider": "Dividers",
  "defaultsDialog.storage": "Storage Units",
  "defaultsDialog.conduit": "Conduits",
  "defaultsDialog.pump": "Pumps",
  "defaultsDialog.orifice": "Orifices",
  "defaultsDialog.weir": "Weirs",
  "defaultsDialog.outlet": "Outlets",
  "defaultsDialog.idIncrement": "ID Increment",
  "defaultsDialog.area": "Area",
  "defaultsDialog.width": "Width",
  "defaultsDialog.slopePercent": "% Slope",
  "defaultsDialog.imperviousPercent": "% Impervious",
  "defaultsDialog.nImperv": "N-Imperv",
  "defaultsDialog.nPerv": "N-Perv",
  "defaultsDialog.dStoreImperv": "Dstore-Imperv",
  "defaultsDialog.dStorePerv": "Dstore-Perv",
  "defaultsDialog.pctZeroImperv": "% of Impervious Area with No Depression Storage",
  "defaultsDialog.invertElevation": "Node Invert Elevation",
  "defaultsDialog.maxDepth": "Node Max. Depth",
  "defaultsDialog.pondedArea": "Node Ponded Area",
  "defaultsDialog.conduitLength": "Conduit Length",
  "defaultsDialog.conduitRoughness": "Conduit Roughness",
  // Generic across every node-placement / two-node-click tool (junction,
  // outfall, storage, divider, raingage, conduit, pump, orifice, weir,
  // outlet) — {kind} is the tool's own kind.* noun (App.tsx's own
  // kindLabelKey), a leading clause rather than grammatically embedded in
  // the sentence itself, since Spanish needs "el"/"la" depending on the
  // kind's own gender (conducto/bomba/orificio/vertedero/descarga aren't
  // all the same gender) and this phrasing sidesteps that entirely.
  "toolbar.hintAddLinkWaiting": "{kind}: click the other end (from {name})",
  "toolbar.hintAddLinkStart": "{kind}: click the start node",
  "toolbar.hintAddNode": "{kind}: click the map to place it",
  "toolbar.hintAddSubcatchment": "Click the map to draw its outline ({count} points so far)",
  "toolbar.hintAddSubcatchmentOne": "Click the map to draw its outline ({count} point so far)",
  "toolbar.finishSubcatchment": "Finish",
  "toolbar.hintVertexIdle": "[ / ] selects a vertex, Insert/+ adds the first one",
  "toolbar.hintVertexFocused":
    "Vertex {index}/{count} — arrows to move, Insert/+ to add another, Delete to remove it, Esc to stop",
  "common.cancel": "Cancel",
  "common.done": "Done",
  "common.ok": "Accept",
  "common.close": "Close",
  "common.discardChangesTitle": "Discard changes?",
  "common.discardChangesMessage": "You have unsaved changes. Closing now will discard them.",
  "common.discardChangesConfirm": "Discard changes",
  "common.keepEditing": "Keep editing",

  // --- ElementBrowser.tsx ---
  "elementBrowser.raingages": "Raingages",
  "elementBrowser.subcatchments": "Subcatchments",
  "elementBrowser.nodes": "Nodes",
  "elementBrowser.links": "Links",
  "elementBrowser.hydrology": "Hydrology",
  "elementBrowser.toggleKindGroup": "Expand/collapse {kind}",
  "elementBrowser.toggleCategoryGroup": "Expand/collapse {category}",
  "elementBrowser.toggleOnMap": "Show/hide on map",
  "elementBrowser.filterPlaceholder": "Filter tree…",
  "elementBrowser.filterNoMatches": "No elements match this filter",
  "elementBrowser.placeholderHint": "Imported with default values — still needs review",

  // --- PropertyPanel.tsx ---
  "propertyPanel.description": "Description",
  "propertyPanel.noProperties": "No properties recorded for this element.",
  "propertyPanel.coverages": "Land uses",
  "propertyPanel.noLandUsesYet":
    "Add a land use first (Project data → Land uses) to assign coverage.",
  "propertyPanel.noCoverages": "No land use assigned yet.",
  "propertyPanel.landUse": "Land use",
  "propertyPanel.percent": "Percent",
  "propertyPanel.addCoverage": "Add land use",
  "propertyPanel.deleteCoverage": "Remove land use",
  "propertyPanel.coveragesNotFull": "Adds up to {total}%, not 100%.",
  "propertyPanel.loadings": "Initial pollutant buildup",
  "propertyPanel.noLoadings":
    "No initial buildup set — falls back to the antecedent dry-time formula.",
  "propertyPanel.initialBuildup": "Initial buildup",
  "propertyPanel.addLoading": "Add loading",
  "propertyPanel.deleteLoading": "Remove loading",
  "propertyPanel.treatment": "Treatment",
  "propertyPanel.noTreatments": "No treatment expression yet.",
  "propertyPanel.treatmentKind": "Type",
  "propertyPanel.treatmentKindRemoval": "Removal (R)",
  "propertyPanel.treatmentKindConcentration": "Concentration (C)",
  "propertyPanel.treatmentExpression": "Expression",
  "propertyPanel.addTreatment": "Add treatment",
  "propertyPanel.deleteTreatment": "Remove treatment",

  // --- ProjectDataDialog.tsx's LID Controls tab (LidControlsEditor.tsx) ---
  "projectData.tabLidControls": "LID Controls",
  "lid.addControl": "Add LID control",
  "lid.deleteControl": "Delete LID control",
  "lid.noControlSelected": "Select a LID control, or add a new one.",
  "lid.type": "Type",
  "lid.typeBC": "Bio-Retention Cell",
  "lid.typeRG": "Rain Garden",
  "lid.typeGR": "Green Roof",
  "lid.typeIT": "Infiltration Trench",
  "lid.typePP": "Porous Pavement",
  "lid.typeRB": "Rain Barrel",
  "lid.typeVS": "Vegetative Swale",
  "lid.typeRD": "Rooftop Disconnection",
  "lid.layerSurface": "Surface",
  "lid.surfaceStorageHeight": "Berm height",
  "lid.surfaceVegFraction": "Vegetation volume fraction",
  "lid.surfaceRoughness": "Surface roughness (Manning's n)",
  "lid.surfaceSlope": "Surface slope (%)",
  "lid.surfaceSideSlope": "Side slope (run/rise)",
  "lid.layerSoil": "Soil",
  "lid.soilThickness": "Thickness",
  "lid.soilPorosity": "Porosity",
  "lid.soilFieldCap": "Field capacity",
  "lid.soilWiltPoint": "Wilting point",
  "lid.soilKSat": "Conductivity",
  "lid.soilKSlope": "Conductivity slope",
  "lid.soilSuction": "Suction head",
  "lid.layerStorage": "Storage",
  "lid.storageThickness": "Thickness",
  "lid.storageVoidRatio": "Void ratio",
  "lid.storageKSat": "Conductivity",
  "lid.storageClogFactor": "Clogging factor",
  "lid.storageCovered": "Covered (no evaporation)",
  "lid.layerPavement": "Pavement",
  "lid.pavementThickness": "Thickness",
  "lid.pavementVoidRatio": "Void ratio",
  "lid.pavementImpervFrac": "Impervious surface fraction",
  "lid.pavementPermeability": "Permeability",
  "lid.pavementClogFactor": "Clogging factor",
  "lid.pavementRegenDays": "Regeneration interval (days)",
  "lid.pavementRegenDegree": "Regeneration degree",
  "lid.layerDrain": "Drain",
  "lid.drainCoeff": "Flow coefficient",
  "lid.drainExpon": "Flow exponent",
  "lid.drainOffset": "Offset height",
  "lid.drainDelay": "Delay (hr)",
  "lid.drainHOpen": "Open head",
  "lid.drainHClose": "Close head",
  "lid.drainCurve": "Flow curve",
  "lid.layerDrainMat": "Drainage mat",
  "lid.drainMatThickness": "Thickness",
  "lid.drainMatVoidRatio": "Void ratio",
  "lid.drainMatRoughness": "Roughness (Manning's n)",
  "lid.layerRemovals": "Extra pollutant removals",
  "lid.noRemovals": "No extra removal rate set.",
  "lid.removalPercent": "Percent removal",
  "lid.addRemoval": "Add removal",
  "lid.deleteRemoval": "Remove",

  // --- PropertyPanel.tsx's LID usage sub-section (LidUsageEditor.tsx) ---
  "lidUsage.title": "LID controls",
  "lidUsage.noControlsYet": "Add a LID control first (Project data → LID Controls) to place one.",
  "lidUsage.noUsage": "No LID control placed on this subcatchment yet.",
  "lidUsage.lidControl": "LID control",
  "lidUsage.number": "Number",
  "lidUsage.area": "Area (per unit)",
  "lidUsage.width": "Width (per unit)",
  "lidUsage.initSat": "Initial saturation (%)",
  "lidUsage.fromImperv": "% of impervious area treated",
  "lidUsage.toPerv": "Send outflow to pervious area",
  "lidUsage.fromPerv": "% of pervious area treated",
  "lidUsage.drainTo": "Drain to (node/subcatchment)",
  "lidUsage.addUsage": "Add LID placement",
  "lidUsage.deleteUsage": "Remove LID placement",

  // --- ProjectDataDialog.tsx's Aquifers tab (AquifersEditor.tsx) ---
  "projectData.tabAquifers": "Aquifers",
  "aquifer.addAquifer": "Add aquifer",
  "aquifer.deleteAquifer": "Delete aquifer",
  "aquifer.noAquiferSelected": "Select an aquifer, or add a new one.",
  "aquifer.porosity": "Porosity",
  "aquifer.wiltingPoint": "Wilting point",
  "aquifer.fieldCapacity": "Field capacity",
  "aquifer.conductivity": "Conductivity",
  "aquifer.conductSlope": "Conductivity slope",
  "aquifer.tensionSlope": "Tension slope",
  "aquifer.upperEvapFrac": "Upper evaporation fraction",
  "aquifer.lowerEvapDepth": "Lower evaporation depth",
  "aquifer.gwRecession": "Groundwater recession",
  "aquifer.bottomElev": "Bottom elevation",
  "aquifer.waterTableElev": "Water table elevation",
  "aquifer.upperMoisture": "Upper zone moisture content",
  "aquifer.evapPattern": "Evaporation pattern",
  "aquifer.evapPatternNone": "None",

  // --- PropertyPanel.tsx's groundwater sub-section (GroundwaterEditor.tsx) ---
  "groundwater.title": "Groundwater flow",
  "groundwater.noAquifersYet": "Add an aquifer first (Project data → Aquifers) to configure this.",
  "groundwater.aquifer": "Aquifer",
  "groundwater.node": "Receiving node",
  "groundwater.surfaceElev": "Ground surface elevation",
  "groundwater.a1": "Groundwater flow coefficient (A1)",
  "groundwater.a1Tooltip":
    "A1 in QL = A1(Hgw−Hcb)^B1 − A2(Hsw−Hcb)^B2 + A3·Hgw·Hsw — scales the aquifer's own water table height (Hgw).",
  "groundwater.b1": "Groundwater flow exponent (B1)",
  "groundwater.b1Tooltip":
    "B1 in the same lateral-flow equation, applied to the aquifer's own water table height (Hgw).",
  "groundwater.a2": "Surface water flow coefficient (A2)",
  "groundwater.a2Tooltip":
    "A2 in the same equation — scales the receiving node's own surface water height (Hsw), which opposes outflow.",
  "groundwater.b2": "Surface water flow exponent (B2)",
  "groundwater.b2Tooltip":
    "B2 in the same equation, applied to the node's own surface water height (Hsw).",
  "groundwater.a3": "Surface-groundwater interaction coefficient (A3)",
  "groundwater.a3Tooltip": "A3 in the same equation — scales the direct Hgw·Hsw interaction term.",
  "groundwater.fixedDepth": "Fixed receiving node water depth",
  "groundwater.nodeElev": "Receiving node invert elevation (auto if blank)",
  "groundwater.bottomElev": "Aquifer bottom elevation override (auto if blank)",
  "groundwater.waterTableElev": "Initial water table elevation override (auto if blank)",
  "groundwater.upperMoisture": "Initial upper zone moisture override (auto if blank)",
  "groundwater.lateralFlow": "Lateral flow expression override",
  "groundwater.deepFlow": "Deep flow expression override",

  // --- ProjectDataDialog.tsx's Snow Packs tab (SnowPacksEditor.tsx) ---
  "projectData.tabSnowPacks": "Snow Packs",
  "snow.addPack": "Add snow pack",
  "snow.deletePack": "Delete snow pack",
  "snow.noPackSelected": "Select a snow pack, or add a new one.",
  "snow.plowable": "Plowable area",
  "snow.impervious": "Impervious area",
  "snow.pervious": "Pervious area",
  "snow.removal": "Snow removal",
  "snow.cmin": "Minimum melt coefficient",
  "snow.cmax": "Maximum melt coefficient",
  "snow.tbase": "Base melt temperature",
  "snow.fwFrac": "Free water fraction of depth",
  "snow.sd0": "Initial snow depth",
  "snow.fw0": "Initial free water in pack",
  "snow.snn0OrSd100": "Depth-100%-cover / area-100%-plowed",
  "snow.sdPlow": "Depth above which plowing occurs",
  "snow.fout": "Fraction plowed out of watershed",
  "snow.fimperv": "Fraction plowed to impervious area",
  "snow.fperv": "Fraction plowed to pervious area",
  "snow.fimelt": "Fraction converted into immediate melt",
  "snow.fsubcatch": "Fraction plowed to another subcatchment",
  "snow.toSubcatch": "Receiving subcatchment",

  // --- ProjectDataDialog.tsx's Climatology tab (ClimatologyEditor.tsx) ---
  "projectData.tabClimatology": "Climatology",
  "climate.jan": "Jan",
  "climate.feb": "Feb",
  "climate.mar": "Mar",
  "climate.apr": "Apr",
  "climate.may": "May",
  "climate.jun": "Jun",
  "climate.jul": "Jul",
  "climate.aug": "Aug",
  "climate.sep": "Sep",
  "climate.oct": "Oct",
  "climate.nov": "Nov",
  "climate.dec": "Dec",
  "climate.evaporation": "Evaporation",
  "climate.evapType": "Source",
  "climate.evapTypeConstant": "Constant",
  "climate.evapTypeMonthly": "Monthly average",
  "climate.evapTypeTimeSeries": "Time series",
  "climate.evapTypeFile": "Climate file",
  "climate.evapTypeTemperature": "Computed from temperature",
  "climate.evapConstantValue": "Value",
  "climate.evapTimeSeries": "Time series",
  "climate.recoveryPattern": "Recovery pattern",
  "climate.recoveryPatternNone": "None",
  "climate.dryOnly": "Only apply evaporation on dry days",
  "climate.temperature": "Temperature",
  "climate.tempSource": "Source",
  "climate.tempSourceNone": "None",
  "climate.tempSourceTimeSeries": "Time series",
  "climate.tempSourceFile": "Climate file",
  "climate.tempTimeSeries": "Time series",
  "climate.tempFileName": "File name",
  "climate.tempFileStartDate": "Start date (blank for the whole file)",
  "climate.snowmelt": "Snowmelt climatology",
  "climate.snowTemp": "Snowfall temperature",
  "climate.tipm": "Antecedent temperature index weight",
  "climate.rnm": "ADC recovery factor",
  "climate.elevation": "Elevation",
  "climate.latitude": "Latitude (degrees)",
  "climate.dtLongitude": "Time offset from local time zone (hr)",
  "climate.adjustments": "Monthly adjustments",
  "climate.adjustTemperature": "Temperature",
  "climate.adjustEvaporation": "Evaporation",
  "climate.adjustRainfall": "Rainfall (%)",
  "climate.adjustConductivity": "Conductivity multiplier",

  // --- ProjectDataDialog.tsx's Transects tab (TransectsEditor.tsx) ---
  "projectData.tabTransects": "Transects",
  "transect.addTransect": "Add transect",
  "transect.deleteTransect": "Delete transect",
  "transect.noTransectSelected": "Select a transect, or add a new one.",
  "transect.nLeft": "Left overbank roughness (n)",
  "transect.nRight": "Right overbank roughness (n)",
  "transect.nChannel": "Main channel roughness (n)",
  "transect.xLeftBank": "Left bank station",
  "transect.xRightBank": "Right bank station",
  "transect.lengthFactor": "Meander length modifier",
  "transect.stationMultiplier": "Station distance multiplier",
  "transect.elevationOffset": "Elevation offset",
  "transect.station": "Station",
  "transect.elevation": "Elevation",
  "transect.addStation": "Add station",

  // --- PropertyPanel.tsx's DWF sub-section (DwfEditor.tsx) ---
  "dwf.title": "Dry weather flow",
  "dwf.noInflows": "No dry weather inflow set yet.",
  "dwf.constituent": "Constituent",
  "dwf.avgValue": "Average value",
  "dwf.pattern1": "Pattern 1",
  "dwf.pattern2": "Pattern 2",
  "dwf.pattern3": "Pattern 3",
  "dwf.pattern4": "Pattern 4",
  "dwf.addInflow": "Add dry weather inflow",
  "dwf.deleteInflow": "Remove dry weather inflow",

  // --- PropertyPanel.tsx's Inflows sub-section (InflowsEditor.tsx) ---
  "inflows.title": "Direct inflows",
  "inflows.noInflows": "No direct inflow set yet.",
  "inflows.timeSeries": "Time series",
  "inflows.type": "Type",
  "inflows.typeFlow": "Flow",
  "inflows.typeConcen": "Concentration",
  "inflows.typeMass": "Mass",
  "inflows.unitsFactor": "Units factor",
  "inflows.scaleFactor": "Scale factor",
  "inflows.baseline": "Baseline",
  "inflows.basePattern": "Baseline pattern",
  "inflows.addInflow": "Add direct inflow",
  "inflows.deleteInflow": "Remove direct inflow",

  // --- ProjectDataDialog.tsx's Unit Hydrographs tab (UnitHydrographsEditor.tsx) ---
  "projectData.tabHydrographs": "Unit Hydrographs",
  "rdii.addHydrograph": "Add unit hydrograph",
  "rdii.deleteHydrograph": "Delete unit hydrograph",
  "rdii.noHydrographSelected": "Select a unit hydrograph, or add a new one.",
  "rdii.rainGage": "Rain gage",
  "rdii.monthAll": "All months",
  "rdii.deleteMonth": "Remove month",
  "rdii.addMonth": "Add month",
  "rdii.responseShort": "Short-term response",
  "rdii.responseMedium": "Medium-term response",
  "rdii.responseLong": "Long-term response",
  "rdii.r": "Fraction of rainfall entering sewer (R)",
  "rdii.t": "Time to peak (hr)",
  "rdii.k": "Recession ratio",
  "rdii.iaMax": "Max. initial abstraction",
  "rdii.iaRecov": "Initial abstraction recovery rate",
  "rdii.iaInit": "Initial abstraction already filled",

  // --- PropertyPanel.tsx's RDII sub-section (RdiiEditor.tsx) ---
  "rdii.inflowTitle": "RDII inflow",
  "rdii.noHydrographsYet":
    "Add a unit hydrograph first (Project data → Unit Hydrographs) to configure this.",
  "rdii.unitHydrograph": "Unit hydrograph",
  "rdii.sewerArea": "Sewer area contributing RDII",

  // --- ProjectDataDialog.tsx's Control Rules tab (ControlRulesEditor.tsx) ---
  "projectData.tabControlRules": "Control Rules",
  "controlRules.addRule": "Add rule",
  "controlRules.deleteRule": "Delete rule",
  "controlRules.noRuleSelected": "Select a rule, or add a new one.",
  "controlRules.bodyHint": "One clause per line: IF/AND/OR/THEN/ELSE/PRIORITY.",
  "controlRules.visualMode": "Visual editor",
  "controlRules.textMode": "Text editor",
  "controlRules.cannotEditVisually":
    "This rule's own text uses a form the visual editor can't represent (a VARIABLE/EXPRESSION reference, or something unrecognized) — edit it as text instead.",

  // --- ControlRuleBuilder.tsx (the structured IF/THEN/ELSE form) ---
  "controlRules.if": "IF",
  "controlRules.and": "AND",
  "controlRules.or": "OR",
  "controlRules.then": "THEN",
  "controlRules.else": "ELSE",
  "controlRules.ifSection": "Conditions (IF)",
  "controlRules.thenSection": "Actions (THEN)",
  "controlRules.elseSection": "Else actions (ELSE)",
  "controlRules.addCondition": "Add condition",
  "controlRules.addAction": "Add action",
  "controlRules.addElseAction": "Add else action",
  "controlRules.deleteCondition": "Delete condition",
  "controlRules.deleteAction": "Delete action",
  "controlRules.priority": "Priority",
  "controlRules.compareToElement":
    "Compare to another element's own attribute instead of a fixed value",
  "controlRules.compareToValue":
    "Compare to a fixed value instead of another element's own attribute",
  "controlRules.compareToKind": "Compare against",
  "controlRules.rhsKindValue": "Value",
  "controlRules.rhsKindElement": "Element",
  "controlRules.statusOpenOn": "Open / On (1)",
  "controlRules.statusClosedOff": "Closed / Off (0)",
  "controlRules.settingNumber": "Fixed value",
  "controlRules.settingCurve": "Curve",
  "controlRules.settingTimeSeries": "Time series",
  // P1 deep-review item 31: VariablePicker's own 3 cascading selects (object
  // type / element name / attribute), reused for a premise's LHS, its
  // optional RHS-as-variable, and an action's own target, plus every other
  // bare select/input this builder's own rows use — none had an accessible
  // name at all.
  "controlRules.objectPicker": "Object type",
  "controlRules.idPicker": "Element name",
  "controlRules.attributePicker": "Attribute",
  "controlRules.statusValue": "Status value",
  "controlRules.comparisonValue": "Comparison value",
  "controlRules.timeValue": "Time value",
  "controlRules.dateValue": "Date value",
  "controlRules.logicOperator": "Logic operator",
  "controlRules.comparisonOperator": "Comparison operator",
  "controlRules.settingKind": "Setting type",
  "controlRules.settingValue": "Setting value",
  "controlRules.curvePicker": "Curve",
  "controlRules.timeSeriesPicker": "Time series",

  // --- ProjectDataDialog.tsx's Streets tab (StreetsEditor.tsx) ---
  "projectData.tabStreets": "Streets",
  "projectData.addStreet": "Add street",
  "projectData.deleteStreet": "Delete street",
  "projectData.noStreetSelected": "Select a street section, or add a new one.",
  "projectData.streetRoadWidth": "Road Width (Tcrown)",
  "projectData.streetCurbHeight": "Curb Height (Hcurb)",
  "projectData.streetCrossSlope": "Cross Slope (Sx), %",
  "projectData.streetRoughness": "Road Roughness",
  "projectData.streetGutterDepression": "Gutter Depression (a)",
  "projectData.streetGutterWidth": "Gutter Width (W)",
  "projectData.streetSides": "Sides",
  "projectData.streetTwoSided": "Two Sided",
  "projectData.streetOneSided": "One Sided",
  "projectData.streetBackingWidth": "Backing Width (Tback)",
  "projectData.streetBackingSlope": "Backing Slope (Sback), %",
  "projectData.streetBackingRoughness": "Backing Roughness",

  // --- ProjectDataDialog.tsx's Inlets tab (InletsEditor.tsx) ---
  "projectData.tabInlets": "Inlets",
  "projectData.addInlet": "Add inlet",
  "projectData.deleteInlet": "Delete inlet",
  "projectData.noInletSelected": "Select an inlet design, or add a new one.",
  "projectData.inletType": "Type",
  "projectData.inletLength": "Length",
  "projectData.inletWidth": "Width",
  "projectData.inletHeight": "Height",
  "projectData.inletGrateType": "Grate Type",
  "projectData.inletOpenAreaFraction": "Open Area Fraction",
  "projectData.inletSplashVelocity": "Splash Velocity",
  "projectData.inletThroatAngle": "Throat Angle",
  "projectData.inletCurveName": "Capture Curve Name",

  // --- PropertyPanel.tsx's Inlet Usage section (InletUsageEditor.tsx) ---
  "propertyPanel.inletUsage": "Inlets",
  "propertyPanel.noInletUsage": "No inlet placed on this conduit yet.",
  "propertyPanel.addInletUsage": "Add inlet",
  "propertyPanel.deleteInletUsage": "Remove inlet",
  "propertyPanel.inletUsageDesign": "Inlet design",
  "propertyPanel.inletUsageNode": "Capture node",
  "propertyPanel.inletUsageNumInlets": "# Inlets",
  "propertyPanel.inletUsagePctClogged": "% Clogged",
  "propertyPanel.inletUsageFlowLimit": "Flow Limit",
  "propertyPanel.inletUsageLocalDepress": "Local Depression",
  "propertyPanel.inletUsageLocalWidth": "Local Width",
  "propertyPanel.inletUsagePlacement": "Placement",
  "propertyPanel.noInletDesignsYet":
    "Add an inlet design first (Project Data → Inlets) to place one here.",
  "propertyPanel.invalidNumber": "Not a number — not saved.",

  // --- NetworkMap.tsx / GeoreferencedMap.tsx ---
  "networkMap.noCoordinates": "This project has no map coordinates to show.",
  "networkMap.loadingNetwork": "Loading network…",
  "networkMap.coordsGeographic": "Lon {lon}°  Lat {lat}°",
  "networkMap.coordsPlane": "X {x}  Y {y}",
  "networkMap.toggleOverview": "Overview map",
  "networkMap.zoomIn": "Zoom in",
  "networkMap.zoomOut": "Zoom out",
  "networkMap.zoomToExtent": "Zoom to full extent",
  // A map element's own accessible name — its kind first (a screen reader
  // otherwise just announces the raw name + "button", with no context on
  // what kind of element it even is).
  "networkMap.elementAriaLabel": "{kind} {name}",
  "map.showLinkDirection": "Show flow direction",

  // --- MapSettingsMenu.tsx ---
  "mapSettings.title": "Map display options",

  // --- ResultsChart.tsx / resultsVariables.ts ---
  "results.depth": "Depth",
  "results.head": "Head",
  "results.volume": "Volume",
  "results.totalInflow": "Total Inflow",
  "results.flooding": "Flooding",
  "results.flow": "Flow",
  "results.velocity": "Velocity",
  "results.capacity": "Capacity",
  "results.runoff": "Runoff",
  "results.gwFlow": "Groundwater Flow",
  "results.precipitation": "Precipitation",
  "results.evaporation": "Evaporation",
  "results.infiltration": "Infiltration",
  "results.viewChart": "Chart",
  "results.viewTable": "Table",
  "results.time": "Time",
  "simulationSummary.title": "Simulation Summary",
  "simulationSummary.runoffContinuity": "Runoff Quantity Continuity",
  "simulationSummary.totalPrecip": "Total Precipitation",
  "simulationSummary.outfallRunon": "Outfall Runon",
  "simulationSummary.evapLoss": "Evaporation Loss",
  "simulationSummary.infilLoss": "Infiltration Loss",
  "simulationSummary.surfaceRunoff": "Surface Runoff",
  "simulationSummary.initLidStorage": "Initial LID Storage",
  "simulationSummary.lidDrainage": "LID Drainage",
  "simulationSummary.finalStorage": "Final Storage",
  "simulationSummary.continuityError": "Continuity Error",
  "simulationSummary.noRunoff": "No rainfall or subcatchments to report on.",
  "simulationSummary.extremes": "Extremes",
  "simulationSummary.topNodeDepths": "Highest Node Depths",
  "simulationSummary.topLinkFlows": "Highest Link Flows",
  "simulationSummary.node": "Node",
  "simulationSummary.link": "Link",
  "simulationSummary.noResults": "No results to report on.",
  "simulationSummary.peakSystemOutflow": "Peak total discharge to outfalls",
  "simulationSummary.exportCsv": "Export CSV",

  // --- StatisticsReportDialog.tsx ---
  "statistics.title": "Statistics Report",
  "statistics.category": "Category",
  "statistics.element": "Element",
  "statistics.variable": "Variable",
  "statistics.statType": "Statistic",
  "statistics.statType.mean": "Mean",
  "statistics.statType.peak": "Peak",
  "statistics.statType.total": "Total (volume)",
  "statistics.statType.duration": "Duration",
  "statistics.minEventValue": "Min. event value",
  "statistics.minEventVolume": "Min. event volume",
  "statistics.minEventDelta": "Min. inter-event time (hours)",
  "statistics.noResults": "No simulation results for this element.",
  "statistics.summary": "Summary",
  "statistics.eventCount": "Number of events",
  "statistics.eventFrequency": "Event frequency",
  "statistics.min": "Minimum",
  "statistics.max": "Maximum",
  "statistics.mean": "Mean",
  "statistics.stdDev": "Std. deviation",
  "statistics.skew": "Skewness",
  "statistics.period": "Analysis period",
  "statistics.period.years": "years",
  "statistics.period.months": "months",
  "statistics.events": "Events",
  "statistics.frequencyPlot": "Frequency Plot",
  "statistics.noEvents": "No events found with the current thresholds.",
  "statistics.rank": "Rank",
  "statistics.startDate": "Start date",
  "statistics.duration": "Duration (hrs)",
  "statistics.value": "Value",
  "statistics.frequencyPercent": "Frequency (%)",
  "statistics.returnPeriod": "Return period",

  "resultsAnimation.play": "Play",
  "resultsAnimation.pause": "Pause",
  "resultsAnimation.timeline": "Simulation time",
  "resultsAnimation.showColor": "Show {kind} colors on the map",
  "resultsAnimation.variablePicker": "{kind} color variable",

  // --- ResultsDashboardPanel.tsx ---
  "resultsDashboard.title": "Results Dashboard",
  "resultsDashboard.addSelection": "Add selection",
  "resultsDashboard.clear": "Clear all",
  "resultsDashboard.remove": "Remove",
  "resultsDashboard.empty":
    'Select one or more elements on the map or in the tree, then click "Add selection".',
  "resultsDashboard.layoutCombined": "Combined",
  "resultsDashboard.layoutGrid": "Grid",

  "longitudinalProfile.title": "Longitudinal Profile",
  "longitudinalProfile.titleWithNodes": "Invert-ground profile: {start} → {end}",
  "longitudinalProfile.startNode": "Start node",
  "longitudinalProfile.endNode": "End node",
  "longitudinalProfile.pickOnMap": "Pick on the map",
  "longitudinalProfile.hintPickStart": "Longitudinal profile: click the start node",
  "longitudinalProfile.hintPickEnd": "Longitudinal profile: click the end node",
  "longitudinalProfile.noPath": "These two nodes aren't connected by any path through the network.",
  "longitudinalProfile.distance": "Distance",
  "longitudinalProfile.ground": "Ground",
  "longitudinalProfile.invert": "Invert",
  "longitudinalProfile.waterSurface": "Water Surface",
  "networkTrace.traceDownstream": "Trace downstream from node",
  "networkTrace.traceUpstream": "Trace upstream from node",
  "networkTrace.hintPickDownstream": "Trace downstream: click the start node",
  "networkTrace.hintPickUpstream": "Trace upstream: click the start node",
  "networkTrace.resultDownstream": 'Downstream of "{name}": {nodes} nodes, {links} links',
  "networkTrace.resultUpstream": 'Upstream of "{name}": {nodes} nodes, {links} links',

  "thematicMap.pickAttribute": "Pick an attribute…",
  "thematicMap.categoryNode": "Node",
  "thematicMap.categoryLink": "Link",
  "thematicMap.categorySubcatchment": "Subcatchment",
  "thematicMap.nodeInvert": "Invert Elevation",
  "thematicMap.nodeMaxDepth": "Max. Depth",
  "thematicMap.linkDiameter": "Diameter",
  "thematicMap.linkLength": "Length",
  "thematicMap.linkRoughness": "Roughness",
  "thematicMap.subArea": "Area",
  "thematicMap.subImperv": "%Impervious",
  "thematicMap.subWidth": "Width",
  "thematicMap.resultNodeMaxDepth": "Max. Depth (result)",
  "thematicMap.resultLinkMaxFlow": "Max. Flow (result)",
  "thematicMap.attributePicker": "Map color attribute",
  "mapQuery.matchCount": "{count} of {total} match",
  "mapQuery.selectMatches": "Select matches",
  "mapQuery.attributePicker": "Query attribute",
  "mapQuery.comparator": "Comparator",
  "mapQuery.threshold": "Threshold value",
  "backgroundImage.title": "Background Image",
  "backgroundImage.file": "Image file",
  "backgroundImage.selectFile": "Select image…",
  "backgroundImage.noFileSelected": "No file selected",
  "backgroundImage.currentImage": "Current image",
  "backgroundImage.noImage": "No background image set — only shown in the plane view.",
  "backgroundImage.lowerLeftX": "Lower-left X",
  "backgroundImage.lowerLeftY": "Lower-left Y",
  "backgroundImage.upperRightX": "Upper-right X",
  "backgroundImage.upperRightY": "Upper-right Y",
  "backgroundImage.useProjectExtent": "Use project extent",
  "backgroundImage.remove": "Remove image",

  // --- ErrorBoundary.tsx ---
  "errorBoundary.title": "Something went wrong",
  "errorBoundary.message":
    "Reload the page to continue. Your project file is unaffected — nothing is saved yet.",
  "errorBoundary.detail": "Technical detail: {message}",
  "errorBoundary.reload": "Reload",

  // --- Element kind labels (PropertyPanel.tsx's "selected.kind" line) ---
  "kind.junction": "Junction",
  "kind.outfall": "Outfall",
  "kind.storage": "Storage",
  "kind.divider": "Divider",
  "kind.conduit": "Conduit",
  "kind.pump": "Pump",
  "kind.orifice": "Orifice",
  "kind.weir": "Weir",
  "kind.outlet": "Outlet",
  "kind.subcatchment": "Subcatchment",
  "kind.raingage": "Raingage",

  // --- Property group section labels (PropertyPanel.tsx's group headers) ---
  "section.JUNCTIONS": "Junction",
  "section.OUTFALLS": "Outfall",
  "section.STORAGE": "Storage",
  "section.DIVIDERS": "Divider",
  "section.CONDUITS": "Conduit",
  "section.PUMPS": "Pump",
  "section.ORIFICES": "Orifice",
  "section.WEIRS": "Weir",
  "section.OUTLETS": "Outlet",
  "section.XSECTIONS": "Cross-section",
  "section.SUBCATCHMENTS": "Subcatchment",
  "section.SUBAREAS": "Subareas",
  "section.INFILTRATION_HORTON": "Infiltration (Horton)",
  "section.INFILTRATION_MODIFIED_HORTON": "Infiltration (Modified Horton)",
  "section.INFILTRATION_GREEN_AMPT": "Infiltration (Green Ampt)",
  "section.INFILTRATION_MODIFIED_GREEN_AMPT": "Infiltration (Modified Green Ampt)",
  "section.INFILTRATION_CURVE_NUMBER": "Infiltration (Curve Number)",
  "section.RAINGAGES": "Raingage",

  // --- Property field (column) labels — SWMM's own .inp vocabulary, see fieldLabels.ts ---
  "field.invertElevation": "Invert Elevation",
  "field.maxDepth": "Max Depth",
  "field.initDepth": "Init Depth",
  "field.surchargeDepth": "Surcharge Depth",
  "field.pondedArea": "Ponded Area",
  "field.outfallType": "Outfall Type",
  "field.stageTableTimeseries": "Stage/Table/Timeseries",
  "field.tideGate": "Tide Gate",
  "field.shape": "Shape",
  "field.coeffCurveA": "Coeff./Curve A",
  "field.coeffB": "Coeff. B",
  "field.coeffC": "Coeff. C",
  "field.evapFraction": "Evap Fraction",
  "field.suctionHead": "Suction Head",
  "field.conductivity": "Conductivity",
  "field.initialDeficit": "Initial Deficit",
  "field.elevation": "Elevation",
  "field.divertedLink": "Diverted Link",
  "field.dividerType": "Divider Type",
  "field.param1": "Param 1",
  "field.param2": "Param 2",
  "field.param3": "Param 3",
  "field.param4": "Param 4",
  "field.param5": "Param 5",
  "field.fromNode": "From Node",
  "field.toNode": "To Node",
  "field.length": "Length",
  "field.roughness": "Roughness",
  "field.inletOffset": "Inlet Offset",
  "field.outletOffset": "Outlet Offset",
  "field.initFlow": "Init Flow",
  "field.maxFlow": "Max Flow",
  "field.pumpCurve": "Pump Curve",
  "field.status": "Status",
  "field.startupDepth": "Startup Depth",
  "field.shutoffDepth": "Shutoff Depth",
  "field.orificeType": "Orifice Type",
  "field.crestHeight": "Crest Height",
  "field.dischargeCoeff": "Discharge Coeff.",
  "field.flapGate": "Flap Gate",
  "field.openCloseTime": "Open/Close Time",
  "field.weirType": "Weir Type",
  "field.endContractions": "End Contractions",
  "field.endCoeff": "End Coeff.",
  "field.ratingType": "Rating Type",
  "field.geom1": "Geom1",
  "field.geom2": "Geom2",
  "field.geom3": "Geom3",
  "field.geom4": "Geom4",
  "field.barrels": "Barrels",
  "field.culvertCode": "Culvert Code",
  "field.raingage": "Raingage",
  "field.subcatchmentOutlet": "Outlet",
  "field.area": "Area",
  "field.percentImperv": "%Imperv",
  "field.width": "Width",
  "field.percentSlope": "%Slope",
  "field.curbLength": "Curb Length",
  "field.snowPack": "Snow Pack",
  "field.nImperv": "N-Imperv",
  "field.nPerv": "N-Perv",
  "field.sImperv": "S-Imperv",
  "field.sPerv": "S-Perv",
  "field.percentZeroImperv": "%Zero-Imperv",
  "field.routeTo": "Route To",
  "field.percentRouted": "%Routed",
  "field.maxRate": "Max Rate",
  "field.minRate": "Min Rate",
  "field.decayConstant": "Decay Constant",
  "field.dryTime": "Dry Time",
  "field.maxInfiltration": "Max Infiltration",
  "field.curveNumber": "Curve Number",
  "field.dryingTime": "Drying Time",
  "field.format": "Format",
  "field.interval": "Interval",
  "field.scf": "SCF",
  "field.sourceType": "Source Type",
  "field.sourceName": "Source Name",

  // --- fieldLabels.ts's OPTION_LABEL_KEYS (real closed-set dropdown values, sectionSchema.ts's own COLUMN_OPTIONS) ---
  "option.subareaOutlet": "Outlet",
  "option.imperv": "Impervious",
  "option.perv": "Pervious",
  "option.off": "Off",
  "option.on": "On",
  "option.dummy": "Dummy",
  "option.circular": "Circular",
  "option.filledCircular": "Filled circular",
  "option.rectClosed": "Closed rectangular",
  "option.rectOpen": "Open rectangular",
  "option.trapezoidal": "Trapezoidal",
  "option.triangular": "Triangular",
  "option.parabolic": "Parabolic",
  "option.power": "Power",
  "option.rectTriangular": "Rectangular triangular",
  "option.rectRound": "Rectangular round",
  "option.modBasketHandle": "Modified basket handle",
  "option.horizEllipse": "Horizontal ellipse",
  "option.vertEllipse": "Vertical ellipse",
  "option.arch": "Arch",
  "option.egg": "Egg",
  "option.horseshoe": "Horseshoe",
  "option.gothic": "Gothic",
  "option.catenary": "Catenary",
  "option.semielliptical": "Semi-elliptical",
  "option.basketHandle": "Basket handle",
  "option.semicircular": "Semicircular",
  "option.irregular": "Irregular",
  "option.custom": "Custom",
  "option.forceMain": "Force main",
  "option.street": "Street",
  "option.cylindrical": "Cylindrical",
  "option.conical": "Conical",
  "option.pyramidal": "Pyramidal",
  "option.cutoff": "Cutoff",
  "option.weir": "Weir",
  "option.overflow": "Overflow",
  "option.tabular": "Tabular",
  "option.functional": "Functional",
  "option.no": "No",
  "option.yes": "Yes",
  "option.side": "Side",
  "option.bottom": "Bottom",
  "option.transverse": "Transverse",
  "option.sideflow": "Side flow",
  "option.vNotch": "V-notch",
  "option.roadway": "Roadway",
  "option.free": "Free",
  "option.normal": "Normal",
  "option.fixed": "Fixed",
  "option.tidal": "Tidal",
  "option.timeseries": "Time series",
  "option.tabularDepth": "Tabular/Depth",
  "option.tabularHead": "Tabular/Head",
  "option.functionalDepth": "Functional/Depth",
  "option.functionalHead": "Functional/Head",
  "option.intensity": "Intensity",
  "option.volume": "Volume",
  "option.cumulative": "Cumulative",
  "option.file": "File",
  "option.none": "None",
  "option.grate": "Grate",
  "option.curb": "Curb",
  "option.slotted": "Slotted",
  "option.dropGrate": "Drop Grate",
  "option.dropCurb": "Drop Curb",
  "option.storage": "Storage",
  "option.rating": "Rating",
  "option.control": "Control",
  "option.diversion": "Diversion",
  "option.shape": "Shape",
  "option.monthly": "Monthly",
  "option.daily": "Daily",
  "option.hourly": "Hourly",
  "option.weekend": "Weekend",
  "option.exponential": "Exponential",
  "option.saturation": "Saturation",
  "option.external": "External",
  "option.eventMeanConcentration": "Event Mean Concentration",
  "option.area": "Area",

  // --- engineWarnings.ts (SimulationResults.warnings translations) ---
  "engineWarning.tooFewColumns":
    '{kind} "{name}" has too few columns to resolve its own {what} and is not modeled.',
  "engineWarning.conduitUnsupportedShape":
    'Conduit "{name}" is not modeled: cross-section shape "{shape}" is not supported.',
  "engineWarning.conduitInvalidLength":
    'Conduit "{name}" has an invalid Length ({length}) and is not modeled.',
  "engineWarning.conduitInvalidManningN":
    'Conduit "{name}" has an invalid Manning\'s n ({manningN}) and is not modeled.',
  "engineWarning.outletUndefinedCurve":
    'Outlet "{name}" is not modeled: its own TABULAR curve "{curve}" is undefined.',
  "engineWarning.outfallTidalCurveUndefined":
    'Outfall "{name}" is TIDAL but its own curve "{curve}" is undefined — treated as FREE instead.',
  "engineWarning.outfallFixedInvalidStage":
    'Outfall "{name}" is FIXED but its own stage value "{stage}" isn\'t a real number — not modeled as an external boundary.',
  "engineWarning.outfallTimeseriesUndefined":
    'Outfall "{name}" is TIMESERIES but its own series "{series}" is undefined or empty — not modeled as an external boundary.',
  "engineWarning.outfallUnrecognizedType":
    'Outfall "{name}" has an unrecognized type ("{type}") — treated as an ordinary interior junction instead of an external boundary.',
  "engineWarning.crownElevationMissingNode":
    '{kind} "{name}" references node "{node}", which doesn\'t exist — its own crown-elevation contribution is skipped.',
  "engineWarning.normalFlowLimitedUnrecognized":
    '[OPTIONS] NORMAL_FLOW_LIMITED has an unrecognized value ("{value}") — using the default ({default}) instead.',
  "engineWarning.invalidNumberUnset":
    '{context}: "{token}" is not a valid number — treated as unset ("*").',
  "engineWarning.resultsResampled":
    "This simulation produced {totalPeriods} report periods, more than the {maxPoints}-point display cap — results were resampled at a {stride}x wider stride across the FULL run instead of being cut off partway through.",
  "engineWarning.gwfDeepExpressionInvalid":
    '[GWF] DEEP expression for subcatchment "{subcatchment}" could not be parsed and was dropped — the default deep-flow formula is used instead.',
  "engineWarning.gwfLateralExpressionInvalid":
    '[GWF] LATERAL expression for subcatchment "{subcatchment}" could not be parsed and was dropped — no addition is made to the default lateral flow.',
  "engineWarning.groundwaterMissingAquifer":
    '[GROUNDWATER] subcatchment "{subcatchment}" references aquifer "{aquifer}", which has no matching [AQUIFERS] row — groundwater is not modeled for it.',
  "engineWarning.inletNotConduit": 'Inlet on "{link}" is not modeled: it is not a modeled conduit.',
  "engineWarning.inletDesignUndefined":
    'Inlet on "{link}" is not modeled: its own design "{inlet}" is undefined or unsupported (CUSTOM).',
  "engineWarning.inletDesignInvalidShape":
    'Inlet on "{link}" is not modeled: its own design "{inlet}" ({designKind}) is not valid on this conduit\'s own cross-section shape.',
  "engineWarning.lidUsageUnknownControl":
    '[LID_USAGE] "{subcatchment}" references LID control "{control}", which has no matching [LID_CONTROLS] row or an unsupported type — this LID unit is not modeled.',
  "engineWarning.lidMissingLayerRow":
    '[LID_CONTROLS] "{name}" ({type}) has no {layer} row — this LID unit is not modeled.',
  "engineWarning.lidMissingPavementRow":
    '[LID_CONTROLS] "{name}" (POROUS_PAVEMENT) has no PAVEMENT row, or its Thickness is 0 — this LID unit is not modeled.',
  "engineWarning.lidUsageInvalidNumberOrArea":
    '[LID_USAGE] "{subcatchment}" / "{control}" has Number<=0 or Area<=0 — this LID unit is not modeled.',
  "engineWarning.raingageMissingInterval":
    '[RAINGAGES] "{name}" has a missing or zero Interval — it will report zero rainfall throughout, even though it has a real rainfall source.',
  "engineWarning.rdiiUnknownHydrograph":
    '[RDII] references unit hydrograph "{name}", which has no matching [HYDROGRAPHS] row — every [RDII] node using it is ignored.',
  "engineWarning.hydrographMissingRainGage":
    '[HYDROGRAPHS] "{name}" has no rain gage assigned — every [RDII] node using it is ignored.',
  "engineWarning.hydrographUnknownRainGage":
    '[HYDROGRAPHS] "{name}" references rain gage "{rainGage}", which has no matching [RAINGAGES] row — every [RDII] node using it is ignored.',
  "engineWarning.invalidNumberFallback":
    '{context}: "{token}" is not a valid number — using {fallback} instead.',
  "engineWarning.inletOnSagUnsupported":
    'Inlet on "{link}" is not modeled: on-sag inlet capture is not supported.',
  "engineWarning.inletDropCurbUnsupportedKW":
    'Inlet on "{link}" is not modeled: DROP_CURB inlet capture is not supported under Kinematic Wave/Steady Flow.',
  "engineWarning.dividerUnsupportedType":
    'Divider "{name}" is not modeled: only CUTOFF, WEIR, TABULAR and OVERFLOW divider types are supported (found "{type}").',
  "engineWarning.controlsExpressionInvalid":
    '[CONTROLS] EXPRESSION "{name}" could not be parsed and was dropped — every premise referencing it resolves as undefined.',
  "engineWarning.controlsRuleUnsupported":
    "[CONTROLS] rule \"{name}\" uses a form Phase 1 doesn't support and was dropped (see controlRuleParser.ts's own header for what's covered).",
  "engineWarning.controlsNoRulesEvaluated":
    "[CONTROLS] rules are present but none could be evaluated — every rule used a form Phase 1 doesn't support (see controlRuleParser.ts's own header for what's covered).",
  "engineWarning.inflowsPollutantUnsupported":
    "[INFLOWS] has a pollutant (CONCEN/MASS) row, which is not modeled: under Kinematic Wave/Steady Flow, this port's own water-quality routing is Dynamic Wave only; under Dynamic Wave, this specific row's own Constituent doesn't match any [POLLUTANTS] name. A pollutant's own external inflow is ignored.",
  "engineWarning.dwfPollutantUnsupported":
    "[DWF] has a pollutant-concentration row, which is not modeled: this port's own dry-weather inflow support is FLOW only. A pollutant's own baseline dry-weather loading is ignored.",
  "engineWarning.raingageFileUnsupported":
    "[RAINGAGES] has a FILE-sourced rain gage, which is not modeled: it reports zero rainfall throughout.",
  "engineWarning.raingageTimeseriesFileUnsupported":
    "[RAINGAGES] uses a [TIMESERIES] that is itself FILE-sourced, which is not modeled: it reports zero rainfall throughout.",
  "engineWarning.evaporationFileUnsupported":
    "[EVAPORATION] FILE is not modeled: potential evaporation is zero throughout instead of being read from an external climate file.",
  "engineWarning.temperatureFileUnsupported":
    "[TEMPERATURE] FILE is not modeled: air temperature (and any snowmelt/temperature-derived evaporation that depends on it) is not read from an external climate file.",
  "engineWarning.storageUnsupportedShape":
    'Storage node "{name}" is not modeled: only FUNCTIONAL, CYLINDRICAL, CONICAL, PARABOLIC, PYRAMIDAL and TABULAR storage shapes are supported (found "{shape}").',
  "engineWarning.storageInvalidShapeCoeffs":
    'Storage node "{name}" ({shape}) has an invalid or out-of-range shape coefficient ({y0}, {y1}, {y2}) and is not modeled.',
  "engineWarning.orificeUnsupportedConfig":
    'Orifice "{name}" is not modeled: only SIDE or BOTTOM orifices with a CIRCULAR or RECT_CLOSED cross-section are supported.',
  "engineWarning.weirUnsupportedConfig":
    'Weir "{name}" is not modeled: only TRANSVERSE/SIDEFLOW/ROADWAY weirs with a RECT_OPEN cross-section, V-NOTCH weirs with a TRIANGULAR cross-section, or TRAPEZOIDAL weirs with a TRAPEZOIDAL cross-section, are supported.',
  "engineWarning.pumpUnsupportedCurveType":
    'Pump "{name}" is not modeled: only PUMP1/PUMP2/PUMP3/PUMP4/PUMP5 curve types are supported.',
  "engineWarning.pollutantUnknownCoPollutant":
    '[POLLUTANTS] "{name}" references co-pollutant "{coPollutant}", which has no matching [POLLUTANTS] row — treated as having no co-pollutant.',
  "engineWarning.landUseUnknown":
    '[{section}] references land use "{landUse}", which has no matching [LANDUSES] row — ignored.',
  "engineWarning.coveragesUnknownLandUse":
    '[COVERAGES] subcatchment "{subcatchment}" references land use "{landUse}", which has no matching [LANDUSES] row — ignored.',
  "engineWarning.treatmentUnknownPollutant":
    '[TREATMENT] node "{node}" references pollutant "{pollutant}", which has no matching [POLLUTANTS] row — that row is ignored.',
  "engineWarning.treatmentInvalidExpression":
    '[TREATMENT] node "{node}" pollutant "{pollutant}" has a malformed expression and was dropped — no treatment is applied for it there.',
  "engineWarning.subcatchmentMissingRow":
    'Subcatchment "{name}" is not modeled: it has no matching [SUBAREAS]/[INFILTRATION] row.',
  "engineWarning.dwStepsNotConverged":
    "Dynamic wave routing: {percent}% of time steps reached the maximum number of trials without converging — check for an unstable or misconfigured element.",
  "engineWarning.dividerUnknownLink":
    'Divider "{name}" is not modeled: its diverted link "{link}" is not a modeled conduit.',

  // --- textRegistry.ts (model/inp/*.ts warnings translations) ---
  "modelWarning.nonFiniteCoordinate":
    '[{section}] row "{name}" has a non-finite coordinate ({x}, {y}).',
  "modelWarning.malformedLinkRow": '[{section}] row "{name}" is missing its own from/to node.',
} as const;

export type TextKey = keyof typeof en;
